import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import type { NavItem } from '../../data/navigation';
import { CompanyMegaPanel, DesignMegaPanel, MarketingMegaPanel } from './MegaMenuPanels';
type NavDropdownProps = {
  label: string;
  mega: NonNullable<NavItem['mega']>;
};

// Full-width mega menu. The panel is positioned against the sticky header,
// so this wrapper is intentionally not `relative`.
export function NavDropdown({
  label,
  mega
}: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const handleClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  const show = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 160);
  };
  const close = () => setOpen(false);
  return <div ref={wrapperRef} onMouseEnter={show} onMouseLeave={hide} data-id="element-683" data-mp="2g9h7cuh2fa5k">
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(v => !v)} className={`flex items-center gap-1 rounded-sm text-sm font-medium transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red ${open ? 'text-brand-red' : 'text-gray-600'}`} data-id="element-684" data-mp="2c842amlsrazp">
        {label}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-685" data-mp="xarpg672tpt0" />
      </button>

      <AnimatePresence data-id="element-686" data-mp="oaw8fkqub0l2">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full z-50 border-t border-gray-100 bg-white shadow-xl" data-id="element-687" data-mp="o9tgralp0u43">
            <div className="w-full px-[clamp(16px,3vw,64px)] py-8" data-id="element-688" data-mp="4mdqno85ypge">
              {mega === 'marketing' && <MarketingMegaPanel onNavigate={close} data-id="element-689" data-mp="4kj3s4dc0n9o" />}
              {mega === 'design' && <DesignMegaPanel onNavigate={close} data-id="element-690" data-mp="9lqas17nhifa" />}
              {mega === 'company' && <CompanyMegaPanel onNavigate={close} data-id="element-691" data-mp="3rsefvr2pa5ky" />}
            </div>
            <div className="border-t border-gray-100 bg-gray-50" data-id="element-692" data-mp="la8gv2n8hc0">
              <div className="flex w-full items-center justify-between gap-6 px-[clamp(16px,3vw,64px)] py-3 text-sm" data-id="element-693" data-mp="mlez2nh436t">
                <span className="text-gray-600" data-id="element-694" data-mp="385jkfwv5bjqo">Not sure where to start? Talk to a real person in Las Vegas.</span>
                <a href="tel:17022134801" className="whitespace-nowrap font-bold text-brand-black transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:underline" data-id="element-695" data-mp="3m5kotmamd9aw">
                  Call (702) 213-4801
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}